/** Shared Event API ↔ calendar UI mappers. */

const DAY_END_MINUTES = 24 * 60;
/** TimeField max is 23:59:59 — used when the UI ends an event at midnight. */
const API_MIDNIGHT_END = '23:59:59';

export function minutesToTimeString(totalMinutes) {
  const capped = Math.min(Math.max(0, Number(totalMinutes) || 0), DAY_END_MINUTES);
  // Django TimeField cannot store 24:00:00
  if (capped >= DAY_END_MINUTES) {
    return API_MIDNIGHT_END;
  }
  const h = Math.floor(capped / 60);
  const m = capped % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:00`;
}

export function timeStringToMinutes(value) {
  const parts = String(value || '0:0').split(':').map(Number);
  const h = Number.isFinite(parts[0]) ? parts[0] : 0;
  const m = Number.isFinite(parts[1]) ? parts[1] : 0;
  if (h >= 24) {
    return DAY_END_MINUTES;
  }
  return h * 60 + m;
}

/** True when the API time represents end-of-day / midnight. */
export function isApiMidnightEnd(value) {
  const parts = String(value || '').split(':').map(Number);
  const h = Number.isFinite(parts[0]) ? parts[0] : 0;
  const m = Number.isFinite(parts[1]) ? parts[1] : 0;
  const s = Number.isFinite(parts[2]) ? parts[2] : 0;
  return h >= 24 || (h === 23 && m === 59 && s >= 59);
}

export function timeStringToHHMM(value) {
  const parts = String(value || '00:00').split(':');
  let h = Number(parts[0]) || 0;
  let m = Number(parts[1]) || 0;
  // Present API midnight marker as 23:59 in month/schedule time fields
  if (isApiMidnightEnd(value)) {
    h = 23;
    m = 59;
  }
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export function hhmmToTimeString(value) {
  const [hRaw, mRaw] = String(value || '00:00').split(':');
  let h = Number(hRaw) || 0;
  let m = Number(mRaw) || 0;
  if (h > 23 || (h === 23 && m > 59) || h === 24) {
    return API_MIDNIGHT_END;
  }
  if (h === 23 && m === 59) {
    return API_MIDNIGHT_END;
  }
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:00`;
}

/** Days between two ISO dates (local). */
export function dayIndexFromIso(iso, weekStartIso) {
  const [y1, m1, d1] = weekStartIso.split('-').map(Number);
  const [y2, m2, d2] = iso.split('-').map(Number);
  const start = Date.UTC(y1, m1 - 1, d1);
  const day = Date.UTC(y2, m2 - 1, d2);
  return Math.round((day - start) / 86400000);
}

/** Add `delta` days to an ISO date string (YYYY-MM-DD). */
export function addDaysToIso(iso, delta) {
  const [y, m, d] = String(iso || '').split('-').map(Number);
  if (!y || !m || !d) return iso;
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + Number(delta || 0));
  const yy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(dt.getUTCDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

/** Inclusive list of ISO dates from startIso to endIso. */
export function eachIsoDay(startIso, endIso) {
  const start = startIso;
  const end = endIso || startIso;
  if (!start) return [];
  if (end < start) return [start];
  const days = [];
  let cur = start;
  while (cur <= end) {
    days.push(cur);
    cur = addDaysToIso(cur, 1);
  }
  return days;
}

/**
 * Expand a timed multi-day event into per-day segments for calendar layout.
 * Each segment keeps `id` as the real event id and adds `segmentKey` / `segmentDate`.
 */
export function expandEventToDaySegments(event, options = {}) {
  const {
    weekStartIso = null,
    startKey = 'start',
    endKey = 'end',
    dateKey = 'date',
    endDateKey = 'endDate',
    useMinutes = true,
  } = options;

  const startDate = event[dateKey] || event.date;
  const endDate = event[endDateKey] || event.endDate || startDate;
  if (!startDate) return [];

  const days = eachIsoDay(startDate, endDate);
  if (days.length === 0) return [];

  const rawStart = event[startKey];
  const rawEnd = event[endKey];
  const startMin = useMinutes
    ? Number(rawStart) || 0
    : timeStringToMinutes(hhmmToTimeString(rawStart));
  let endMin = useMinutes
    ? Number(rawEnd) || 0
    : isApiMidnightEnd(hhmmToTimeString(rawEnd))
      ? DAY_END_MINUTES
      : timeStringToMinutes(hhmmToTimeString(rawEnd));

  if (days.length === 1) {
    const dayIndex =
      weekStartIso != null ? dayIndexFromIso(startDate, weekStartIso) : event.day;
    return [
      {
        ...event,
        segmentKey: `${event.id}::${startDate}`,
        segmentDate: startDate,
        rangeStart: startDate,
        rangeEnd: endDate,
        endDate,
        date: startDate,
        day: dayIndex,
        start: useMinutes ? startMin : timeStringToHHMM(minutesToTimeString(startMin)),
        end: useMinutes
          ? endMin
          : timeStringToHHMM(minutesToTimeString(endMin)),
        continuesFromPrev: false,
        continuesToNext: false,
        isMultiDay: false,
      },
    ];
  }

  return days.map((iso, index) => {
    const isFirst = index === 0;
    const isLast = index === days.length - 1;
    let segStart = 0;
    let segEnd = DAY_END_MINUTES;
    if (isFirst) segStart = startMin;
    if (isLast) segEnd = endMin;
    if (!isFirst && !isLast) {
      segStart = 0;
      segEnd = DAY_END_MINUTES;
    }

    const dayIndex =
      weekStartIso != null ? dayIndexFromIso(iso, weekStartIso) : undefined;

    return {
      ...event,
      segmentKey: `${event.id}::${iso}`,
      segmentDate: iso,
      rangeStart: startDate,
      rangeEnd: endDate,
      endDate,
      date: iso,
      day: dayIndex,
      start: useMinutes ? segStart : timeStringToHHMM(minutesToTimeString(segStart)),
      end: useMinutes ? segEnd : timeStringToHHMM(minutesToTimeString(segEnd)),
      continuesFromPrev: !isFirst,
      continuesToNext: !isLast,
      isMultiDay: true,
    };
  });
}

export function apiEventToWeekEvent(apiEvent, weekStartIso) {
  const iso = apiEvent.day;
  const endIso = apiEvent.end_day || apiEvent.day;
  return {
    id: apiEvent.id,
    day: dayIndexFromIso(iso, weekStartIso),
    date: iso,
    endDate: endIso,
    title: apiEvent.title || '',
    start: timeStringToMinutes(apiEvent.start),
    end: isApiMidnightEnd(apiEvent.end)
      ? DAY_END_MINUTES
      : timeStringToMinutes(apiEvent.end),
    category: apiEvent.category || 'HO',
  };
}

export function weekEventToApiPayload(event, weekDays) {
  const iso =
    event.date ||
    weekDays?.find((d) => d.day === event.day)?.iso ||
    null;
  const endIso = event.endDate || iso;
  return {
    day: iso,
    end_day: endIso,
    title: (event.title || '').trim(),
    start: minutesToTimeString(event.start),
    end: minutesToTimeString(event.end),
    category: event.category || 'HO',
  };
}

export function apiEventToMonthEvent(apiEvent) {
  return {
    id: apiEvent.id,
    date: apiEvent.day,
    endDate: apiEvent.end_day || apiEvent.day,
    title: apiEvent.title || '',
    start: timeStringToHHMM(apiEvent.start),
    end: timeStringToHHMM(apiEvent.end),
    category: apiEvent.category || 'HO',
  };
}

export function monthEventToApiPayload(event) {
  const endDate = event.endDate || event.date;
  return {
    day: event.date,
    end_day: endDate,
    title: (event.title || '').trim(),
    start: hhmmToTimeString(event.start),
    end: hhmmToTimeString(event.end),
    category: event.category || 'HO',
  };
}

/** Compare two ISO date + HH:MM (or minutes) pairs; return true if end > start. */
export function isEndAfterStart(startIso, startTime, endIso, endTime, { minutes = false } = {}) {
  if (!startIso || !endIso) return false;
  const startMin = minutes
    ? Number(startTime) || 0
    : timeStringToMinutes(String(startTime || '00:00'));
  const endMin = minutes
    ? Number(endTime) || 0
    : timeStringToMinutes(String(endTime || '00:00'));
  if (endIso > startIso) return true;
  if (endIso < startIso) return false;
  return endMin > startMin;
}
